import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Box, Tooltip } from '@mui/material';
import * as XLSX from 'xlsx-js-style';
import {
  getStoredCompanyDetails,
  getCompanyDisplayFromDetails,
  getShortCompanyName,
} from '../../services/companyService';

/** Split label into up to `maxLines` lines (newline-separated) for table/Excel headers. */
function wrapLabelToLines(label, maxLines = 3) {
  if (!label) return '';
  const words = String(label).split(/\s+/).filter(Boolean);
  if (words.length <= maxLines) return words.join('\n');
  const perLine = Math.ceil(words.length / maxLines);
  const lines = [];
  for (let i = 0; i < words.length; i += perLine) {
    lines.push(words.slice(i, i + perLine).join(' '));
  }
  return lines.slice(0, maxLines).join('\n');
}

function GratuityReportGenerated() {
  const location = useLocation();
  const companyDetails =
    useSelector((state) => state.auth.activeCompanyDetails) ||
    getStoredCompanyDetails();
  const { companyName, addressLine1, addressLine2 } =
    getCompanyDisplayFromDetails(companyDetails || {});

  const reportData = location.state?.reportData || {};
  const baseDate = reportData.base_date || location.state?.baseDate || '';

  const data = reportData.data || [];
  const financialYear = reportData.financial_year || '';
  const accumulatedLabel =
    reportData.accumulated_gratuity_label ||
    `Accumulated Gratuity Amount as on ${baseDate} Rs`;
  const gratuityFYLabel =
    reportData.gratuity_for_financial_year_label ||
    `Gratuity for FY ${financialYear} Rs`;
  // Format base_date as "Mar 2026" — handles both DD-MM-YYYY and YYYY-MM-DD
  const formatHeaderDate = (dateStr) => {
    if (!dateStr) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    // DD-MM-YYYY (e.g. "31-03-2026")
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [, mm, yyyy] = dateStr.split('-').map(Number);
      return `${months[mm - 1]} ${yyyy}`;
    }
    // YYYY-MM-DD fallback
    const date = new Date(dateStr);
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Format date_of_joining as dd-MMM-yyyy (e.g. 31-Mar-2026)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, '0');
    const mmm = months[date.getMonth()];
    const yyyy = date.getFullYear();
    return `${dd}-${mmm}-${yyyy}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return Number(num).toLocaleString('en-IN');
  };

  const formatEmployeeName = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';
    const parts = trimmed.split(/\s+/).filter(Boolean);
    const last = parts[parts.length - 1] || '';
    const doubled = last ? [...parts, last] : parts;
    return doubled.join(' ').toUpperCase();
  };

  const totals = data.reduce(
    (acc, emp) => ({
      basic: acc.basic + Number(emp.basic || 0),
      service_years: acc.service_years + Number(emp.service_years || 0),
      service_months: acc.service_months + Number(emp.service_months || 0),
      service_days: acc.service_days + Number(emp.service_days || 0),
      completed_months_of_service:
        acc.completed_months_of_service + Number(emp.completed_months_of_service || 0),
      completed_years_of_service:
        acc.completed_years_of_service + Number(emp.completed_years_of_service || 0),
      accumulated_gratuity_amount:
        acc.accumulated_gratuity_amount + Number(emp.accumulated_gratuity_amount || 0),
      gratuity_for_financial_year:
        acc.gratuity_for_financial_year + Number(emp.gratuity_for_financial_year || 0),
    }),
    {
      basic: 0,
      service_years: 0,
      service_months: 0,
      service_days: 0,
      completed_months_of_service: 0,
      completed_years_of_service: 0,
      accumulated_gratuity_amount: 0,
      gratuity_for_financial_year: 0,
    }
  );

  const headerDate = formatHeaderDate(baseDate);
  const numCols = 12;

  // ── Styled Excel download using xlsx-js-style (matches PFReport / ESIReport) ──
  const handleExcelDownload = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [];

    const headerNames = [
      'Sr. No.',
      'Employee Code',
      'Employee Name',
      'Basic',
      'Date of Joining',
      'Service Years',
      'Service Months',
      'Service Days',
      'Completed Months of Service',
      'Completed Years of Service',
      wrapLabelToLines(accumulatedLabel, 3),
      wrapLabelToLines(gratuityFYLabel, 2),
    ];

    const fullDataRows = [];
    data.forEach((emp, i) => {
      fullDataRows.push([
        i + 1,
        emp.employee_code || '',
        emp.employee_name || '',
        emp.basic != null ? Number(emp.basic) : 0,
        formatDate(emp.date_of_joining),
        emp.service_years != null ? Number(emp.service_years) : 0,
        emp.service_months != null ? Number(emp.service_months) : 0,
        emp.service_days != null ? Number(emp.service_days) : 0,
        emp.completed_months_of_service ?? 0,
        emp.completed_years_of_service ?? 0,
        emp.accumulated_gratuity_amount != null ? Number(emp.accumulated_gratuity_amount) : 0,
        emp.gratuity_for_financial_year != null ? Number(emp.gratuity_for_financial_year) : 0,
      ]);
    });
    fullDataRows.push([
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      'GRAND TOTAL',
      totals.accumulated_gratuity_amount,
      totals.gratuity_for_financial_year,
    ]);

    // Row 0: Company name
    wsData.push([companyName || '', ...Array(numCols - 1).fill('')]);
    // Row 1: Address line 1
    wsData.push([addressLine1 || '', ...Array(numCols - 1).fill('')]);
    // Row 2: Address line 2
    wsData.push([addressLine2 || '', ...Array(numCols - 1).fill('')]);
    // Row 3: Report title + base date — fully merged, centered
    wsData.push([`Gratuity as on ${baseDate}`, ...Array(numCols - 1).fill('')]);
    // Row 4: Column headers
    wsData.push(headerNames);
    // Rows 5+: Data
    fullDataRows.forEach((row) => wsData.push(row));

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto column widths (skip first 4 header rows)
    const colWidths = [];
    for (let col = 0; col < numCols; col++) {
      let maxWidth = 0;
      wsData.forEach((row, rowIndex) => {
        if (rowIndex < 4) return;
        if (row[col] !== undefined && row[col] !== null) {
          maxWidth = Math.max(maxWidth, String(row[col]).length);
        }
      });
      // Give wider width to long label columns
      let wch = Math.max(maxWidth + 2, 10);
      if (col === 10) wch = Math.min(Math.max(wch, 14), 18); // accumulated column narrower
      if (col === 11) wch = Math.min(Math.max(wch, 14), 18); // FY column narrower
      colWidths.push({ wch });
    }
    ws['!cols'] = colWidths;
    ws['!rows'] = ws['!rows'] || [];
    ws['!rows'][4] = { hpt: 42 };

    // Merge rows 0-3 across all columns (like company name/address rows)
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: numCols - 1 } },
    ];

    // Border style
    const borderStyle = {
      top:    { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left:   { style: 'thin', color: { rgb: '000000' } },
      right:  { style: 'thin', color: { rgb: '000000' } },
    };

    // Company name style — bold, 16pt, centered
    const companyNameStyle = {
      font:      { bold: true, sz: 16, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border:    borderStyle,
    };

    // Column header style — bold light text on near-black (matches on-screen table)
    const columnHeaderStyle = {
      font:      { bold: true, color: { rgb: 'F1F3F5' } },
      fill:      { fgColor: { rgb: '0D0F12' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border:    borderStyle,
    };

    const dataStyle = {
      border:    borderStyle,
      alignment: { vertical: 'center' },
    };

    // Apply styles to company / title header rows (0-3) — all fully merged
    for (let row = 0; row <= 3; row++) {
      const cellAddr = XLSX.utils.encode_cell({ r: row, c: 0 });
      if (ws[cellAddr]) ws[cellAddr].s = companyNameStyle;
      // Delete empty merged cells to avoid visible grid lines
      for (let col = 1; col < numCols; col++) {
        const emptyAddr = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[emptyAddr]) delete ws[emptyAddr];
      }
    }

    // Apply styles to column header row (row 4)
    for (let col = 0; col < numCols; col++) {
      const cellAddr = XLSX.utils.encode_cell({ r: 4, c: col });
      if (ws[cellAddr]) ws[cellAddr].s = columnHeaderStyle;
    }

    // Apply styles to data rows (row 5+)
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let row = 5; row <= range.e.r; row++) {
      for (let col = 0; col < numCols; col++) {
        const cellAddr = XLSX.utils.encode_cell({ r: row, c: col });
        if (ws[cellAddr]) {
          const isTotalRow = row === range.e.r;
          let horizontal = 'left';
          if (isTotalRow) {
            if (col === 10 || col === 11) horizontal = 'right';
            else horizontal = 'left';
          } else if (col === 0 || col === 4) {
            horizontal = 'center';
          } else if (col >= 3 && col !== 4) {
            horizontal = 'right';
          }

          ws[cellAddr].s = {
            ...dataStyle,
            font: { bold: isTotalRow },
            alignment: { ...dataStyle.alignment, horizontal },
          };

          const numFmtCols = [3, 5, 6, 7, 8, 9, 10, 11];
          if (!isTotalRow && numFmtCols.includes(col)) {
            ws[cellAddr].z = '#,##0';
          } else if (isTotalRow && (col === 10 || col === 11)) {
            ws[cellAddr].z = '#,##0';
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Gratuity Report');
    XLSX.writeFile(
      wb,
      `${getShortCompanyName(companyName) || 'Company'} Gratuity Report ${headerDate}.xlsx`
    );
  };

  /* UI table: same pattern as Combined Salary — wide table, first 3 cols sticky, rest scroll horizontally */
  const allColumns = [
    { key: 'srNo', name: 'Sr. No.', width: '60px' },
    { key: 'employeeCode', name: 'Employee Code', width: '120px' },
    { key: 'employeeName', name: 'Employee Name', width: '180px' },
    { key: 'basic', name: 'Basic', width: '100px' },
    { key: 'doj', name: 'Date of Joining', width: '120px' },
    { key: 'serviceYears', name: 'Service Years', width: '100px' },
    { key: 'serviceMonths', name: 'Service Months', width: '100px' },
    { key: 'serviceDays', name: 'Service Days', width: '100px' },
    { key: 'completedMonths', name: 'Completed Months of Service', width: '140px' },
    { key: 'completedYears', name: 'Completed Years of Service', width: '140px' },
    { key: 'accumulated', name: accumulatedLabel, width: '200px' },
    { key: 'gratuityFy', name: wrapLabelToLines(gratuityFYLabel, 2), width: '145px' },
  ];
  const fixedColumnWidths = allColumns.slice(0, 3).map((c) => c.width);
  const totalTableWidth = allColumns.reduce((sum, c) => sum + parseInt(c.width, 10), 0);

  const getAlign = (key) => {
    if (key === 'employeeCode' || key === 'employeeName') return 'left';
    if (key === 'srNo' || key === 'doj') return 'center';
    return 'right';
  };

  const getDataCell = (emp, key, rowIndex) => {
    switch (key) {
      case 'srNo':
        return rowIndex + 1;
      case 'employeeCode':
        return emp.employee_code || '';
      case 'employeeName':
        return formatEmployeeName(emp.employee_name);
      case 'basic':
        return formatNumber(emp.basic);
      case 'doj':
        return formatDate(emp.date_of_joining);
      case 'serviceYears':
        return emp.service_years ?? '-';
      case 'serviceMonths':
        return emp.service_months ?? '-';
      case 'serviceDays':
        return emp.service_days ?? '-';
      case 'completedMonths':
        return emp.completed_months_of_service ?? '-';
      case 'completedYears':
        return emp.completed_years_of_service ?? '-';
      case 'accumulated':
        return formatNumber(emp.accumulated_gratuity_amount);
      case 'gratuityFy':
        return formatNumber(emp.gratuity_for_financial_year);
      default:
        return '';
    }
  };

  /* Header label: up to ~3 wrapped lines inside <th> (clamp on inner div only — keeps table layout stable) */
  const headerLabelWrapStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    lineHeight: 1.25,
    maxHeight: '3.75em',
    whiteSpace: 'normal',
  };
  /* Last column: 2-line header via newlines from wrapLabelToLines (pre-line, not -webkit-box) */
  const headerLabelWrapStyleLastCol = {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    lineHeight: 1.25,
  };

  /* Vertical rule after every column; thicker line after last sticky col (before scroll area) */
  const borderRightHeader = (index) =>
    index === 2 ? '2px solid #adb5bd' : '1px solid #3a3f46';
  const headerBarBg = '#0d0f12';
  const borderRightBody = (index) =>
    index === 2 ? '2px solid #dee2e6' : '1px solid #dee2e6';

  /* Grand total: label in 3rd-from-last col; sums only in last 2 money columns */
  const getGrandTotalCell = (key) => {
    switch (key) {
      case 'completedYears':
        return 'GRAND TOTAL';
      case 'accumulated':
        return formatNumber(totals.accumulated_gratuity_amount);
      case 'gratuityFy':
        return formatNumber(totals.gratuity_for_financial_year);
      default:
        return '';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: '10px',
        overflowX: 'hidden',
        maxWidth: '100vw',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '100%', padding: '20px', minWidth: 0, overflow: 'hidden' }}>
        <Button variant="contained" color="success" onClick={handleExcelDownload} sx={{ marginBottom: 2 }}>
          Download Gratuity Report (Excel)
        </Button>

        {data.length === 0 ? (
          <Box sx={{ textAlign: 'center', padding: 4, border: '1px solid #dee2e6', borderRadius: '4px' }}>
            No eligible employees found for the selected base date.
          </Box>
        ) : (
          <div
            style={{
              height: '600px',
              width: '100%',
              maxWidth: '100%',
              overflow: 'auto',
              overflowX: 'auto',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              backgroundColor: '#fff',
              isolation: 'isolate',
            }}
          >
            <table
              style={{
                display: 'table',
                overflow: 'visible',
                whiteSpace: 'normal',
                width: `${totalTableWidth}px`,
                minWidth: `${totalTableWidth}px`,
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                margin: 0,
              }}
              className="table"
            >
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  background: '#212529',
                  color: 'white',
                  boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)',
                }}
              >
                <tr>
                  {allColumns.map((col, index) => {
                    const isFixed = index < 3;
                    const left = isFixed
                      ? index === 0
                        ? 0
                        : fixedColumnWidths.slice(0, index).reduce((s, w) => s + parseInt(w, 10), 0)
                      : undefined;
                    const align = getAlign(col.key);
                    return (
                      <th
                        key={col.key}
                        style={{
                          width: col.width,
                          minWidth: col.width,
                          padding: '12px 8px',
                          boxSizing: 'border-box',
                          textAlign: align,
                          verticalAlign: 'middle',
                          whiteSpace: 'normal',
                          borderTop: 'none',
                          borderLeft: 'none',
                          borderBottom: 'none',
                          borderRight: borderRightHeader(index),
                          background: headerBarBg,
                          ...(isFixed && {
                            position: 'sticky',
                            left: `${left}px`,
                            zIndex: 11,
                          }),
                        }}
                      >
                        <Tooltip
                          title={col.key === 'gratuityFy' ? gratuityFYLabel : col.name}
                          placement="top"
                          arrow
                        >
                          <div
                            style={
                              col.key === 'gratuityFy' ? headerLabelWrapStyleLastCol : headerLabelWrapStyle
                            }
                          >
                            {col.name}
                          </div>
                        </Tooltip>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.map((emp, i) => (
                  <tr key={emp.employee_code || i} style={{ height: '48px', backgroundColor: 'white' }}>
                    {allColumns.map((col, index) => {
                      const isFixed = index < 3;
                      const left = isFixed
                        ? index === 0
                          ? 0
                          : fixedColumnWidths.slice(0, index).reduce((s, w) => s + parseInt(w, 10), 0)
                        : undefined;
                      const align = getAlign(col.key);
                      const value = getDataCell(emp, col.key, i);
                      return (
                        <td
                          key={col.key}
                          style={{
                            width: col.width,
                            minWidth: col.width,
                            padding: '12px 8px',
                            boxSizing: 'border-box',
                            textAlign: align,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            borderBottom: '1px solid #dee2e6',
                            borderRight: borderRightBody(index),
                            ...(isFixed && {
                              position: 'sticky',
                              left: `${left}px`,
                              zIndex: 5,
                              background: 'white',
                            }),
                          }}
                          title={col.key === 'employeeName' ? String(emp.employee_name || '') : undefined}
                        >
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr style={{ height: '48px', backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  {allColumns.map((col, index) => {
                    const isFixed = index < 3;
                    const left = isFixed
                      ? index === 0
                        ? 0
                        : fixedColumnWidths.slice(0, index).reduce((s, w) => s + parseInt(w, 10), 0)
                      : undefined;
                    const value = getGrandTotalCell(col.key);
                    const align =
                      col.key === 'completedYears'
                        ? 'left'
                        : getAlign(col.key);
                    return (
                      <td
                        key={col.key}
                        style={{
                          width: col.width,
                          minWidth: col.width,
                          padding: '12px 8px',
                          boxSizing: 'border-box',
                          textAlign: align,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid #dee2e6',
                          borderRight: borderRightBody(index),
                          ...(isFixed && {
                            position: 'sticky',
                            left: `${left}px`,
                            zIndex: 5,
                            background: '#f8f9fa',
                          }),
                        }}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Summary below table */}
        
      </Box>
    </Box>
  );
}

export default GratuityReportGenerated;
